import { AppModule, AppProject, Claim, Feature, FilterAttributeType } from './page-config.models';
import { WidgetID } from './widget.model';

export interface AutocompleteFilterOption {
  name: string;
  value: any;
}

export enum FilterType {
  DATE,
  AUTOCOMPLETE,
  AUTOCOMPLETE_TREE,
  PLANNING_DATE,
}

export enum FilterParent {
  SURVEY = 'survey',
  ALIGNMENT = 'alignment',
  INTERACTION = 'interaction',
  COACH = 'coach',
  ACTIVITY_RESPONSES = 'activity-responses',
  MIR = 'mir',
  UNIVERSAL = 'universal',
}

export interface DateableRequest {
  startDate: string;
  endDate: string;
  request?: string;
  projectId?: string;
}

export interface CommonFilterRequest extends DateableRequest {
  [key: string]: any;
}

export interface AutocompleteFilterModel {
  options: AutocompleteFilterOption[];
  label: string;
  property: string;
  overrideParentFilter?: FilterParent;
  requiredFilters?: FilterValidationValues;
  validationMessage?: string;
  toolTip?: string;
}

export interface GroupedFilterModel {
  label: string;
  property: string;
  overrideParentFilter?: FilterParent;
  includeAlignment?: boolean;
}

export interface ToggleCitationsPayload extends DatasetID {
  visible: boolean;
}

export interface ToggleFiltersPayload extends DatasetID {
  visible: boolean;
}

export interface FilterConfigPayload {
  filters: FilterConfig[];
  datasetId?: string;
}

export interface ToggleAIInsightsPayload extends DatasetID {
  visible: boolean;
}

export interface FilterConfig {
  claims: Claim[];
  features: Feature[];
  filterAttributeType?: FilterAttributeType;
  label: string;
  overrideParentFilter?: FilterParent;
  property: CommonFilterList | string;
  requiredFilters?: FilterValidationValues;
  toolTip?: string;
  type: FilterType;
  validationMessage?: string;
  visibilityCheck?: (activeProject: AppProject, activeModule?: AppModule) => boolean;
}

export interface CitationsModel {
  [key: string]: Citations | null;
}

export interface Citations {
  visible: boolean;
}
export interface FilterBuilderModel {
  [key: string]: FilterBuilder;
}

export interface FilterBuilder {
  visible: boolean;
  filters: FilterConfig[];
  customFilters: FilterConfig[];
  hideAIInsights?: boolean;
}

export interface FilterValueObject {
  name: string;
  value: string;
  propertyName?: string;
}

export interface CommonFilterObject {
  type?: FilterType;
  label?: string;
  chartFilterId?: string;
  value: any;
}

export interface DatasetID {
  datasetId: string;
}

export type DatasetIDComponentProp = {
  datasetId?: string;
  showTooltip?: boolean;
  removable?: boolean;
  textClass?: string;
};

export interface CommonFilterKeyPayload extends DatasetID {
  filterKey: string;
}

export interface CommonFilterPayload extends DatasetID {
  filter: CommonFilterPayloadAction;
}
export interface CommonFiltersPayload extends DatasetID {
  filters: CommonFilterPayloadAction[] | CommonFiltersPayloadAction[];
}

export interface CommonFilterPayloadAction {
  key: string;
  label?: string;
  filter: CommonFilterObject;
  type: FilterType;
}

export interface CommonFiltersPayloadAction {
  key: string;
  label?: string;
  filters: CommonFilterObject[];
  type: FilterType;
}

export type FilterValidationValues = CommonFilterList | CommonFilterList[];

export interface CommonFilterModel {
  [key: string]: CommonFilter | null;
}

export interface CommonFiltersItem {
  [key: string]: CommonFilterObject;
}

export interface CommonFilter {
  filters: CommonFiltersItem;
  appliedFilters: CommonFiltersItem;
}

export enum CommonFilterList {
  AlignmentChange = 'AlignmentChange',
  CategoryStatus = 'SurveyIsActive',
  Channel = 'channelType',
  Country = 'accountCountry',
  FieldTeam = 'FieldTeam',
  InteractionType = 'InteractionType',
  ManagerName = 'Manager',
  Product = 'Product',
  Profile = 'Profile',
  Question = 'Question',
  Response = 'ResponseOption',
  Sentiment = 'SentimentLabel',
  Specialty = 'specialtyName',
  Survey = 'Survey',
  Tags = 'Tags',
  Team = 'Team',
  Territory = 'Territory',
  Topic = 'Topic',
  PreviousAlignment = 'PreviousAlignmentScoreLabel',
  AlignmentScore = 'AlignmentScoreLabel',
  InsightSourceName = 'InsightSourceName',
}

export enum SegmentFilterList {
  AlignmentScore = 'AlignmentScore',
  PreviousAlignmentScore = 'PreviousAlignmentScore',
  DiseaseState = 'DiseaseState',
  Keyword = 'Keyword',
  Total = 'total',
  Treatment = 'Treatment',
  Country = 'accountCountryAbbreviation',
  Duration = 'Duration',
}

export interface FilterListRequestParameters {
  filterString: CommonFilterList | string;
  startDate: string;
  endDate: string;
  questionId?: number;
  filterParent: FilterParent;
  includeAlignment?: boolean;
}

export interface ChartFilterPayload extends DatasetID {
  chartFilter: ChartFilterModel;
}

export interface ChartFilterModel {
  propertyName: string;
  propertyDisplayName: string;
  propertyDisplayValue?: string;
  value: any;
  id: WidgetID;
  uniqueCohort?: string;
}

export interface FilterListEntry {
  id: number;
  displayName: string;
  latestInteractionEngagement?: string;
  latestResponseEngagement?: string;
}
